import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, Sparkles, LayoutTemplate } from 'lucide-react';
import Modal from '@/components/Common/Modal';
import { Button } from '@/components/Common/button';
import notify from '@/lib/notify';
import { translateApiError } from '@/lib/apiError';
import {
  documentTemplateService,
  DocumentTemplate,
  DocumentTemplateFormat,
} from '@/services/documentTemplateService';
import type { SmartPptxMode } from '@/services/artifactsService';

/** The user's generation choice emitted on confirm. */
export interface GenerateFileChoice {
  mode: SmartPptxMode;
  template_id: string | null;
  instructions: string | null;
}

interface GenerateFileDialogProps {
  open: boolean;
  /** Target deliverable format — drives copy and the template picker filter. */
  format: DocumentTemplateFormat;
  onClose: () => void;
  /**
   * Perform the actual generation. Should reject (throw) on failure so the
   * dialog can surface a friendly toast; resolves on success (dialog closes).
   */
  onConfirm: (choice: GenerateFileChoice) => Promise<void>;
}

/**
 * Pre-generation choice dialog for smart document generation (GL).
 *
 * Lets the user pick between an autonomous generation (org charter / structured
 * synthesis) and a generation constrained by one of the org's document
 * templates for the chosen format, with optional free-text instructions. Shared
 * by the GenieArtifact toolbar and the chat composer, for every deliverable
 * format (md/docx/pdf/pptx). PPTX strings live under the `pptx` object of the
 * `common` namespace; the document variants under `generateFile`.
 */
export function GenerateFileDialog({
  open,
  format,
  onClose,
  onConfirm,
}: GenerateFileDialogProps) {
  const { t } = useTranslation();
  const isPptx = format === 'pptx';
  const [mode, setMode] = useState<SmartPptxMode>('autonomous');
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [instructions, setInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset the form each time the dialog opens, then load the org's templates for
  // this format.
  useEffect(() => {
    if (!open) return;
    setMode('autonomous');
    setSelectedTemplateId('');
    setInstructions('');
    setSubmitting(false);

    let cancelled = false;
    setLoadingTemplates(true);
    documentTemplateService
      .listByFormat(format)
      .then((data) => {
        if (cancelled) return;
        setTemplates(data);
      })
      .catch((error) => {
        console.error('Failed to load document templates:', error);
        if (!cancelled) setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplates(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, format]);

  const hasTemplates = templates.length > 0;
  const selectedTemplate = templates.find((tpl) => tpl.id === selectedTemplateId) ?? null;
  const confirmDisabled =
    submitting || (mode === 'template' && (!hasTemplates || !selectedTemplateId));

  // Format-specific copy: presentations keep the existing `pptx.*` wording; the
  // document formats (md/docx/pdf) use the neutral `generateFile.*` variants.
  const copy = {
    title: isPptx
      ? t('pptx.title', 'Générer une présentation')
      : t('generateFile.title', 'Générer un document'),
    description: isPptx
      ? t('pptx.description', 'Choisissez comment générer votre présentation PowerPoint.')
      : t('generateFile.description', 'Choisissez comment générer votre document.'),
    modeAutonomous: isPptx
      ? t('pptx.modeAutonomous', "Générer automatiquement (charte de l'organisation)")
      : t('generateFile.modeAutonomous', 'Générer automatiquement'),
    modeAutonomousHint: isPptx
      ? t(
          'pptx.modeAutonomousHint',
          "L'IA construit un diaporama à partir du contenu, aux couleurs et logos de votre organisation.",
        )
      : t(
          'generateFile.modeAutonomousHint',
          "L'IA met en forme le contenu en un document structuré et complet.",
        ),
    modeTemplateHint: isPptx
      ? t('pptx.modeTemplateHint', 'Remplir un modèle PowerPoint existant de votre organisation.')
      : t(
          'generateFile.modeTemplateHint',
          'Utiliser un modèle de document de votre organisation.',
        ),
    noTemplates: isPptx
      ? t('pptx.noTemplates', 'Aucun modèle PowerPoint disponible pour votre organisation.')
      : t('generateFile.noTemplates', 'Aucun modèle disponible pour ce format.'),
  };

  const handleConfirm = async () => {
    if (confirmDisabled) return;
    const trimmed = instructions.trim();
    const choice: GenerateFileChoice = {
      mode,
      template_id: mode === 'template' ? selectedTemplateId : null,
      instructions: trimmed ? trimmed : null,
    };

    setSubmitting(true);
    try {
      await onConfirm(choice);
      onClose();
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const message =
        // 424: the org's AI provider is unusable (missing/refused key). Its
        // message names the provider, which no generic text here can do.
        status === 424
        ? translateApiError(error, t('generateFile.errorGeneric', 'La génération du document a échoué.'))
        : status === 403
          ? isPptx
            ? t('pptx.errorForbidden', "Vous n'avez pas accès à la génération de présentations.")
            : t('generateFile.errorForbidden', "Vous n'avez pas accès à la génération de documents.")
          : status === 409
            ? isPptx
              ? t('pptx.errorNoContent', 'Aucun contenu à mettre en présentation.')
              : t('generateFile.errorNoContent', 'Aucun contenu à mettre en document.')
            : status === 422
              ? t('pptx.errorTemplateRequired', 'Veuillez sélectionner un modèle.')
              : status === 501
                ? isPptx
                  ? t('pptx.errorLibMissing', "La génération PowerPoint n'est pas disponible sur le serveur.")
                  : t('generateFile.errorLibMissing', "La génération de ce format n'est pas disponible sur le serveur.")
                : status === 502
                  ? isPptx
                    ? t('pptx.errorBadPlan', "La présentation n'a pas pu être structurée. Veuillez réessayer.")
                    : t('generateFile.errorBadPlan', "Le document n'a pas pu être structuré. Veuillez réessayer.")
                  : isPptx
                    ? t('pptx.errorGeneric', 'La génération de la présentation a échoué.')
                    : t('generateFile.errorGeneric', 'La génération du document a échoué.');
      notify.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const optionBaseClass =
    'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors';

  return (
    <Modal
      open={open}
      title={copy.title}
      description={copy.description}
      onClose={() => {
        if (!submitting) onClose();
      }}
      className="max-w-lg"
      hideDefaultFooter
    >
      <div className="space-y-4">
        {/* Autonomous option */}
        <label
          className={`${optionBaseClass} ${
            mode === 'autonomous'
              ? 'border-primary bg-primary/5'
              : 'border-border hover:bg-muted/40'
          }`}
        >
          <input
            type="radio"
            name="generate-file-mode"
            className="mt-1 h-4 w-4"
            checked={mode === 'autonomous'}
            onChange={() => setMode('autonomous')}
            disabled={submitting}
          />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
              <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              {copy.modeAutonomous}
            </span>
            <span className="mt-1 block text-xs text-muted-foreground">
              {copy.modeAutonomousHint}
            </span>
          </span>
        </label>

        {/* Template option */}
        <label
          className={`${optionBaseClass} ${
            !hasTemplates
              ? 'border-border opacity-60 cursor-not-allowed'
              : mode === 'template'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:bg-muted/40'
          }`}
        >
          <input
            type="radio"
            name="generate-file-mode"
            className="mt-1 h-4 w-4"
            checked={mode === 'template'}
            onChange={() => setMode('template')}
            disabled={submitting || !hasTemplates}
          />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
              <LayoutTemplate className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              {t('pptx.modeTemplate', 'Utiliser un modèle')}
            </span>
            <span className="mt-1 block text-xs text-muted-foreground">
              {hasTemplates
                ? copy.modeTemplateHint
                : loadingTemplates
                  ? t('pptx.loadingTemplates', 'Chargement des modèles...')
                  : copy.noTemplates}
            </span>

            {/* Template picker — shown only when the template mode is active. */}
            {mode === 'template' && hasTemplates && (
              <span className="mt-2 block space-y-1.5">
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  disabled={submitting}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  aria-label={t('pptx.templateLabel', 'Modèle')}
                >
                  <option value="" disabled>
                    {t('pptx.templatePlaceholder', 'Sélectionnez un modèle')}
                  </option>
                  {templates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>
                      {tpl.name}
                    </option>
                  ))}
                </select>
                {selectedTemplate?.description && (
                  <span className="block text-xs text-muted-foreground">
                    {selectedTemplate.description}
                  </span>
                )}
              </span>
            )}
          </span>
        </label>

        {/* Optional instructions */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-foreground">
            {t('pptx.instructionsLabel', 'Instructions (facultatif)')}
          </label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            disabled={submitting}
            rows={3}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            placeholder={t('pptx.instructionsPlaceholder', 'Consignes supplémentaires pour la génération...')}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('pptx.cancel', 'Annuler')}
          </Button>
          <Button onClick={() => void handleConfirm()} disabled={confirmDisabled}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {submitting ? t('pptx.generating', 'Génération en cours...') : t('pptx.confirm', 'Générer')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default GenerateFileDialog;
